import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { authMe, setUserType } from "store/slices/authSlice";
import Loading from "components/shared-components/Loading";
import { initializeSocket, disconnectSocket } from "services/socketService";
import { initializeSocketEventHandlers, cleanupSocketEventHandlers } from "services/socketEventHandler";

/**
 * AuthInitializer - Centralized auth state synchronization
 * 
 * This component ensures auth state is fully loaded from localStorage
 * and synced with Redux BEFORE any routing logic executes.
 * 
 * Responsibilities:
 * 1. Load token, userType, userData from localStorage on mount
 * 2. Sync with Redux store
 * 3. Fetch fresh user data from API if token exists
 * 4. Show loading screen until auth state is ready
 * 5. Prevent UI flicker and wrong redirects on refresh
 */
const AuthInitializer = ({ children }) => {
  const dispatch = useDispatch();
  const { token, userData, userType, loading } = useSelector((state) => state.auth);
  const [isInitializing, setIsInitializing] = useState(true);

  useEffect(() => {
    const initializeAuth = async () => {
      const storedToken = localStorage.getItem("auth_token");
      const storedUserType = localStorage.getItem("USER_TYPE");
      const storedUserData = localStorage.getItem("USER_DATA");

      if (storedToken) {
        if (storedUserType && !userType) {
          dispatch(setUserType(storedUserType));
        }

        if (!userData || !userData._id) {
          try {
            await dispatch(authMe()).unwrap();
          } catch (error) {
            localStorage.removeItem("auth_token");
            localStorage.removeItem("USER_TYPE");
            localStorage.removeItem("USER_DATA");
          }
        }

        const socket = initializeSocket(storedToken);
        initializeSocketEventHandlers();
      }

      setIsInitializing(false);
    };

    initializeAuth();
    
    // Cleanup socket when component unmounts or user logs out
    return () => {
      cleanupSocketEventHandlers();
      disconnectSocket();
    };
  }, [dispatch]); // Only run once on mount

  // Show loading screen while initializing
  if (isInitializing) {
    return <Loading cover="page" />;
  }

  return children;
};

export default AuthInitializer;
